import {
  Outlet,
  RouteObject as ReactRouterRouteObject,
  createBrowserRouter as reactRouterCreateBrowserRouter,
} from 'react-router-dom';
import { ErrorBoundary } from '~/components';
import {
  UserRouteGuard,
  UserRouteGuardSSO,
  OrganizationRoleRouteGuard,
  OrganizationRouteGuard,
  OrganizationSubscriptionRouteGuard,
  WorkspaceRoleRouteGuard,
  WorkspaceRouteGuard,
} from './guard';
import { Restrictions, RouteObject, RestrictedRouteObject } from './types';
import { RouteContext } from './Context';

interface RestrictionsObject {
  restrictions?: Restrictions;
}

type ReactRouterRestrictionsRouteObject = ReactRouterRouteObject & RestrictionsObject;

const errorBoundaryElement = (
  <ErrorBoundary>
    <Outlet />
  </ErrorBoundary>
);

const parseRouteGuards = (
  route: ReactRouterRestrictionsRouteObject,
  routeWithGuards?: ReactRouterRouteObject,
): ReactRouterRouteObject => {
  if (route.restrictions?.workspace) {
    const { workspace, ...partialRestrictions } = route.restrictions;
    return parseRouteGuards(
      { ...route, restrictions: partialRestrictions },
      {
        element: <WorkspaceRouteGuard restrictions={workspace} type={partialRestrictions.action} />,
        children: [route],
      },
    );
  }

  if (route.restrictions?.workspaceRole) {
    const { workspaceRole, ...partialRestrictions } = route.restrictions;
    return parseRouteGuards(
      { ...route, restrictions: partialRestrictions },
      {
        element: (
          <WorkspaceRoleRouteGuard restriction={workspaceRole} type={partialRestrictions.action} />
        ),
        children: [route],
      },
    );
  }

  if (route.restrictions?.organization) {
    const { organization, ...partialRestrictions } = route.restrictions;
    return parseRouteGuards(
      { ...route, restrictions: partialRestrictions },
      {
        element: (
          <OrganizationRouteGuard restrictions={organization} type={partialRestrictions.action} />
        ),
        children: [route],
      },
    );
  }

  if (route.restrictions?.whitelistSubscription) {
    const { whitelistSubscription, ...partialRestrictions } = route.restrictions;
    return parseRouteGuards(
      { ...route, restrictions: partialRestrictions },
      {
        element: (
          <OrganizationSubscriptionRouteGuard
            restrictions={whitelistSubscription}
            mode="whitelist"
            type={partialRestrictions.action}
          />
        ),
        children: [route],
      },
    );
  }

  if (route.restrictions?.blacklistSubscription) {
    const { blacklistSubscription, ...partialRestrictions } = route.restrictions;
    return parseRouteGuards(
      { ...route, restrictions: partialRestrictions },
      {
        element: (
          <OrganizationSubscriptionRouteGuard
            restrictions={blacklistSubscription}
            mode="blacklist"
            type={partialRestrictions.action}
          />
        ),
        children: [route],
      },
    );
  }

  if (route.restrictions?.organizationRole) {
    const { organizationRole, ...partialRestrictions } = route.restrictions;
    return parseRouteGuards(
      { ...route, restrictions: partialRestrictions },
      {
        element: (
          <OrganizationRoleRouteGuard
            restriction={organizationRole}
            type={partialRestrictions.action}
          />
        ),
        children: [route],
      },
    );
  }

  if (route.restrictions?.user) {
    const { user, ...partialRestrictions } = route.restrictions;
    return parseRouteGuards(
      { ...route, restrictions: partialRestrictions },
      {
        element: <UserRouteGuard restrictions={user} type={partialRestrictions.action} />,
        children: [route],
      },
    );
  }

  if (route.restrictions?.userRole) {
    const { userRole, ...partialRestrictions } = route.restrictions;
    return parseRouteGuards(
      { ...route, restrictions: partialRestrictions },
      {
        element: <UserRouteGuardSSO restriction={userRole} type={partialRestrictions.action} />,
        children: [route],
      },
    );
  }

  return {
    element: errorBoundaryElement,
    children: [routeWithGuards ?? route],
  };
};

const parseRoute = ({
  lazy: lazyImport,
  lazyRoute: lazy,
  category,
  title,
  ...route
}: RestrictedRouteObject) => {
  if (!lazyImport) return { ...route, lazy, handle: { title, category } };

  return {
    ...route,
    handle: { title, category },
    lazy: async () => {
      const { default: Component } = await lazyImport();

      return {
        Component,
      };
    },
  };
};

const parseRouteTabs = (routes: readonly RestrictedRouteObject[] = []) => {
  const filteredRoutes = routes.filter((route) => route.tab);

  if (filteredRoutes.length === 0) return undefined;
  return filteredRoutes.reduce<
    Record<
      string,
      { order: number; restrictions: RestrictedRouteObject['restrictions']; path: string }
    >
  >((acc, current, index) => {
    acc[current.path || 'root'] = {
      order: index,
      restrictions: current.restrictions,
      path: current.path ?? '',
    };
    return acc;
  }, {});
};

/**
 * Parse modified routes and make them react router compatible
 */
const parseRoutes = (routes: readonly RestrictedRouteObject[]): ReactRouterRouteObject[] =>
  routes.map((originalRoute) => {
    const { children, ...parsedRoute } = parseRoute(originalRoute);
    const route = parsedRoute.index
      ? parseRouteGuards(parsedRoute)
      : parseRouteGuards({
          ...parsedRoute,
          ...(children !== undefined && { children: parseRoutes(children) }),
          index: parsedRoute.index,
        });

    const tabs = parseRouteTabs(originalRoute.children);
    if (!tabs) return route;

    return {
      element: <RouteContext tabs={tabs} />,
      children: [route],
    };
  });

/**
 * Custom control room router that adds logic for analytics tracking and permission validation
 */
export const createBrowserRouter = (routes: readonly RouteObject[]) =>
  reactRouterCreateBrowserRouter(parseRoutes(routes));
